import SvgInfo from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import { commasForNumbers } from './helper';
import { makeStyles } from 'tss-react/mui';
import {
  BuildScenarioRowResult,
  RowType,
  TableType,
} from '../../pages/FinancialForensicsPage/FinancialForensics.types';
import {
  VariableCost,
  VendorEntry,
} from '../../pages/FinancialForensicsPage/types/VariableCost.types';
import {
  FixedCost,
  Service,
  Vendor,
} from '../../pages/FinancialForensicsPage/types/FixedCost.types';

export type Scenario = {
  _id: string;
  egNid: string;
  createdBy: string;
  scenarioName: string;
  notes?: string;
  planStartDate: string | Date;
  planEndDate: string | Date;
  totalFixedCost: number;
  totalVariableCost: number;
  createdAt: string | Date;
  updatedAt: string | Date;
};

const useStyles = makeStyles()({
  icon: {
    height: '16px',
    width: '16px',
    margin: '0px 8px 0px 0px',
    '& path': {
      stroke: patientAccessTheme.palette.warning.A600,
    },
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  underline: {
    '&:hover': {
      color: patientAccessTheme.palette.primary.main + ' !important',
      textDecoration: 'underline dashed',
      textUnderlineOffset: '0.25rem',
      cursor: 'pointer',
    },
  },
});

export const BuildScenario: React.FC<{
  handleCurrent: any;
  handleBuildScenario: any;
  value: number;
  handleScenario?: () => void;
  row?: any;
  cellValue?: any;
  tableType?: TableType;
}> = ({
  handleCurrent,
  handleBuildScenario,
  handleScenario,
  row,
  cellValue,
  tableType,
}) => {
  const { classes } = useStyles();

  const key = tableType === 'FIXED' ? 'fees' : 'value';
  const displayValue = cellValue?.[key];

  return displayValue !== 0 && displayValue !== undefined ? (
    <Typography
      fontSize="14px"
      fontType="regular"
      textColor={patientAccessTheme.palette.text.mediumemphasis}
      className={classes.underline}
      onClick={handleScenario}
    >
      ${commasForNumbers(displayValue?.toString() || '0', 2)}
    </Typography>
  ) : !(row?.[key] === 0 && displayValue === 0) ? (
    <div style={{ display: 'flex', gap: '8px' }}>
      <Typography
        fontSize="14px"
        fontType="semiBold"
        textColor={patientAccessTheme.palette.primary[400]}
        styleProps={{
          textDecoration: '1.5px underline dashed',
          textUnderlineOffset: '6px',
          cursor: 'pointer',
        }}
        onClick={handleCurrent}
      >
        Use Current
      </Typography>
      <div
        style={{
          width: '1px',
          background: patientAccessTheme.palette.grey[300],
        }}
      ></div>
      <Typography
        fontSize="14px"
        fontType="semiBold"
        textColor={patientAccessTheme.palette.primary[400]}
        styleProps={{
          textDecoration: '1.5px underline dashed',
          textUnderlineOffset: '6px',
          cursor: 'pointer',
        }}
        onClick={handleBuildScenario}
      >
        Build Scenario
      </Typography>
    </div>
  ) : (
    <Typography
      fontSize="14px"
      textColor={patientAccessTheme.palette.warning.A600}
      className={classes.flex}
    >
      <SvgInfo className={classes.icon} />
      No Details Found
    </Typography>
  );
};

export const scenarioColumns = [
  { accessor: 'scenarioName', name: 'Scenario Name', width: 200 },
  { accessor: 'baseLineYear', name: 'Basline Year', width: 150 },
  {
    accessor: 'createdBy',
    name: 'Created By',
    width: 200,
  },
  {
    accessor: 'createdAt',
    name: 'Created On',
    width: 150,
    cell: (value: any) => {
      return <>{new Date(value).toISOString().split('T')[0]}</>;
    },
  },
  {
    accessor: 'updatedAt',
    name: 'Last Edited',
    width: 150,
    cell: (value: any) => {
      return <>{new Date(value).toISOString().split('T')[0]}</>;
    },
  },
  {
    accessor: 'totalFixedCost',
    name: 'Total Fixed ($)',
    width: 180,
    cell: (value: any) => {
      return <>{commasForNumbers(value?.toString(), 2)}</>;
    },
  },
  {
    accessor: 'totalVariableCost',
    name: 'Total Variable ($)',
    width: 200,
    cell: (value: any) => {
      return <>{commasForNumbers(value?.toString(), 2)}</>;
    },
  },
  {
    accessor: 'totalCost',
    name: 'Grand Total ($)',
    width: 200,
    cell: (value: any) => {
      return <>{commasForNumbers(value?.toString(), 2)}</>;
    },
  },
];

export const formatDate = (date: string | Date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    year: '2-digit',
  });
};

export const resetFeesToZero = (input: FixedCost): FixedCost => {
  const updated = JSON.parse(JSON.stringify(input));

  // Reset fees in planContribution.fees
  updated.planContribution.fees =
    updated.planContribution.fees?.map((vendor: Vendor) => ({
      ...vendor,
      services:
        vendor.services?.map((service: Service) => ({
          ...service,
          fees: 0,
        })) ?? [],
    })) ?? [];

  // Reset fees in planContribution.credits
  updated.planContribution.credits =
    updated.planContribution.credits?.map((vendor: Vendor) => ({
      ...vendor,
      services:
        vendor.services?.map((service: Service) => ({
          ...service,
          fees: 0,
        })) ?? [],
    })) ?? [];

  // Reset fees in memberContribution
  updated.memberContribution =
    updated.memberContribution?.map((vendor: Vendor) => ({
      ...vendor,
      services:
        vendor.services?.map((service: Service) => ({
          ...service,
          fees: 0,
        })) ?? [],
    })) ?? [];

  return updated;
};
export const resetVariableCostValuesToZero = (
  input: VariableCost,
): VariableCost => {
  const updated = JSON.parse(JSON.stringify(input));

  updated.planContribution.fees =
    updated.planContribution.fees?.map((vendor: VendorEntry) => ({
      ...vendor,
      value: 0,
    })) ?? [];

  updated.planContribution.rebates =
    updated.planContribution.rebates?.map((vendor: VendorEntry) => ({
      ...vendor,
      value: 0,
    })) ?? [];

  updated.memberContribution =
    updated.memberContribution?.map((vendor: VendorEntry) => ({
      ...vendor,
      value: 0,
    })) ?? [];

  return updated;
};

export const getRowType = (
  rowType: RowType,
  tableType: TableType,
): BuildScenarioRowResult => {
  if (rowType === 'member') {
    return 'Member';
  }

  const map: Record<TableType, Record<RowType, BuildScenarioRowResult>> = {
    FIXED: {
      primary: 'Fees',
      secondary: 'Credits',
      member: 'Member',
      tertiary: '',
    },
    VARIABLE: {
      primary: 'Fees',
      secondary: 'Rebates',
      member: 'Member',
      tertiary: '',
    },
  };

  return map[tableType]?.[rowType] ?? '';
};

export const normalizeFeeCalculation = (feeCalculation: any, label: string) => {
  if (!feeCalculation?.details) {
    return feeCalculation;
  }

  if (
    feeCalculation.details[0] &&
    !Array.isArray(feeCalculation.details[0]) &&
    feeCalculation.details[0].values
  ) {
    return feeCalculation;
  }

  if (Array.isArray(feeCalculation.details[0])) {
    return {
      ...feeCalculation,
      details: [
        {
          type: label,
          values: feeCalculation.details[0],
        },
      ],
    };
  }

  return feeCalculation;
};
